import { z } from "zod";
import { toVnDateString } from "@apv-erp/status";

/**
 * Form nhật ký máy — tên trường camelCase khớp machineLogs.create/update (packages/trpc/src/routers/schemas.ts).
 * Trước đây form gửi snake_case (machine_id, issue_type...) + ca "Ca 1/2/3" → máy chủ luôn trả 400 (B-01).
 * Mã nhật ký (logId) do máy chủ sinh. Ca = mã cấu hình "shifts" (CA_1...).
 */
export const machineLogSchema = z.object({
    logId: z.string().optional(),
    machineId: z.string().min(1, "Vui lòng chọn máy"),
    productionOrderId: z.string().optional(),
    date: z.string().min(1, "Vui lòng chọn ngày"),
    shift: z.string().min(1, "Vui lòng chọn ca"),
    type: z.enum(["OPERATION", "DOWNTIME", "MAINTENANCE", "SETUP"]), // khớp MachineLogType

    durationMinutes: z.coerce.number().int("Thời gian phải là số phút nguyên").min(1, "Thời gian phải lớn hơn 0"),
    operatorId: z.string().optional(),

    // Operation Specific
    parameters: z.object({
        speed: z.string().optional(),
        temperature: z.string().optional(),
        pressure: z.string().optional(),
    }).optional(),

    qtyOutput: z.coerce.number().min(0, "Sản lượng không được âm").default(0),
    qtyWaste: z.coerce.number().min(0, "Phế phẩm không được âm").default(0),
    targetQty: z.coerce.number().optional(), // Used for validation

    // NG Specific
    attachments: z.array(z.string()).optional().default([]),

    // Downtime Specific
    issueType: z.string().optional(),
    reason: z.string().optional(),
    fixAction: z.string().optional(),
}).superRefine((data, ctx) => {
    if (data.type === "DOWNTIME") {
        if (!data.issueType) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Chọn loại sự cố",
                path: ["issueType"]
            });
        }
        if (!data.reason) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Nhập lý do dừng máy",
                path: ["reason"]
            });
        }
    }

    if (data.type === "OPERATION") {
        // NG Validation
        if (data.qtyWaste > 0) {
            if (!data.reason?.trim() && (!data.attachments || data.attachments.length === 0)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Vui lòng nhập lý do hoặc đính kèm ảnh khi có hàng lỗi (NG)",
                    path: ["reason"]
                });
            }
        }

        // Target QTY validation
        if (data.targetQty !== undefined && data.targetQty > 0) {
            const total = data.qtyOutput + data.qtyWaste;
            if (total > data.targetQty) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Tổng SL OK và NG (${total}) không được vượt quá số lượng sản xuất (${data.targetQty})`,
                    path: ["qtyOutput"]
                });
            }
        }
    }
});

export type MachineLogFormValues = z.infer<typeof machineLogSchema>;

/** Payload gửi machineLogs.create / machineLogs.update (logId không gửi — máy chủ sinh / giữ nguyên). */
export function toMachineLogPayload(v: MachineLogFormValues) {
    const isOperation = v.type === "OPERATION";
    const parameters: Record<string, unknown> = {};
    if (isOperation) {
        for (const [k, val] of Object.entries(v.parameters || {})) {
            if (val) parameters[k] = val;
        }
        parameters.qtyOutput = v.qtyOutput || 0;
        parameters.qtyWaste = v.qtyWaste || 0;
        if (v.attachments && v.attachments.length > 0) parameters.attachments = v.attachments;
    }
    return {
        machineId: v.machineId,
        productionOrderId: v.productionOrderId || null,
        date: v.date,
        shift: v.shift,
        type: v.type,
        durationMinutes: Number(v.durationMinutes),
        operatorId: v.operatorId || null,
        parameters: Object.keys(parameters).length > 0 ? parameters : null,
        issueType: isOperation ? null : v.issueType || null,
        reason: v.reason || null,
        fixAction: isOperation ? null : v.fixAction || null,
    };
}

/** Bản ghi nhật ký máy từ API → giá trị form (để sửa). */
export function machineLogToFormValues(log: any): Partial<MachineLogFormValues> {
    if (!log) return {};
    const p = (log.parameters && typeof log.parameters === "object" ? log.parameters : {}) as Record<string, any>;
    return {
        logId: log.logId || "",
        machineId: log.machineId || log.machine?.id || "",
        productionOrderId: log.productionOrderId || "",
        date: log.date ? toVnDateString(new Date(log.date)) : toVnDateString(),
        shift: log.shift || "",
        type: (["OPERATION", "DOWNTIME", "MAINTENANCE"].includes(log.type) ? log.type : "OPERATION") as any,
        durationMinutes: Number(log.durationMinutes ?? 0),
        operatorId: log.operatorId || log.operator?.id || "",
        parameters: { speed: p.speed || "", temperature: p.temperature || "", pressure: p.pressure || "" },
        qtyOutput: Number(p.qtyOutput || 0),
        qtyWaste: Number(p.qtyWaste || 0),
        attachments: Array.isArray(p.attachments) ? p.attachments : [],
        issueType: log.issueType || "",
        reason: log.reason || "",
        fixAction: log.fixAction || "",
    };
}
